import { createI18n } from 'vue-i18n';

/** Supported locales; tet is the default (no URL prefix). */
export const SUPPORTED_LOCALES = ['tet', 'en', 'pt'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: SupportedLocale = 'tet';

/**
 * Derive locale from URL path. Default locale (tet) has no prefix;
 * others use /{locale}/ at the start (e.g. /en/, /pt/).
 */
export function getLocaleFromPath(pathname: string): SupportedLocale {
  const path = pathname.startsWith('/') ? pathname : `/${pathname}`;
  const segments = path.split('/').filter(Boolean);
  const first = segments[0]?.toLowerCase();
  if (first === 'en' || first === 'pt') return first;
  return DEFAULT_LOCALE;
}

/** Path without `/{en|pt}/` prefix; default locale (tet) has no prefix. Used to detect locale-only navigations. */
export function stripLocalePrefix(pathname: string): string {
  const path = pathname.startsWith('/') ? pathname : `/${pathname}`;
  const segments = path.split('/').filter(Boolean);
  const first = segments[0]?.toLowerCase();
  if (first === 'en' || first === 'pt') {
    const rest = segments.slice(1);
    if (rest.length === 0) return '/';
    return `/${rest.join('/')}`;
  }
  return path === '' ? '/' : path;
}

const i18n = createI18n({
  legacy: false,
  locale: DEFAULT_LOCALE,
  fallbackLocale: DEFAULT_LOCALE,
  messages: {
    tet: {
      common: {
        connectionRequired: 'Presiza ligasaun internet.',
        offlineMutationsHint:
          'Aksaun ne\'ebé presiza ligasaun ba servidor la disponível to\'ó ita liga tan.',
        dismiss: 'Taka',
      },
      nav: {
        language: 'Lian:',
        selectLanguage: 'Hili lian',
        changeLanguage: 'Hili lian',
        search: 'Buka…',
        searchNoResults: 'La iha pájina ne\'ebé tuir husi buka',
        searchLoading: 'Buka hela…',
        searchClear: 'Hamoos buka',
        searchResultsList: 'Rezultadu buka',
        searchGoToLocale: 'Ba pájina iha lian {locale}',
        login: 'Login',
        loginAccount: 'Login / Ha\'u nia konta',
        localeName: {
          tet: 'Tetum',
          en: 'English',
          pt: 'Português',
        },
        contentLocaleMismatchHint: 'Pájina iha {locale}',
        contentLocaleMismatchTitle:
          'Konteúdu pájina iha {content}; menú no etiketa uza lian {ui}.',
      },
      account: {
        none: 'Laiha',
        loginTitleFallback: 'Bem-vindu ba INDMO',
        loginMessageFallback:
          'Aksesu ha\'u nia konta atu jere sertifikasaun sira, hare programa treinamentu nian, no konekta ho rede edukasaun vokasionál Timor-Leste nian.',
        loginImageAlt: 'Treinamentu vokasionál INDMO',
        savedHelpQueriesTitle: 'Ita-boot nia pedidu ajuda iha aparelhu ida-ne\'e',
        savedHelpQueriesHint:
          'Rai ona bainhira ita-boot submit tiha la asesu ho konta. Habuka pedidu ida atu hare mensajen ka hakerek fila fali.',
        savedHelpQueriesLoading: 'Karrega hela…',
        webauthn: {
          notAllowed:
            'Tama kansela ona ka ita-boot nia aparellu la permite.',
          requestFailed: 'Pedidu passkey la bele.',
          genericError: 'Buat ida-ne\'e sala.',
        },
        authCard: {
          signInTitle: 'Tama',
          createTitle: 'Kria konta',
          signInSubtitle: 'Asesu ita-boot nia konta INDMO',
          createSubtitle: 'Mai hamutuk komunidade INDMO',
          tabPassword: 'Email & Seña',
          tabLink: 'Kódigu ba email',
          tabPasskey: 'Passkey',
          tabCreate: 'Kria konta',
          termsSignIn:
            'Ho asesu, ita-boot konkorda ho termus servisu no polítika privasidade INDMO nian.',
          termsCreate:
            'Ho kria konta, ita-boot konkorda ho termus servisu no polítika privasidade INDMO nian.',
          backToHome: '← Fila ba pájina inisiu',
          rememberMe: 'Hanoin ha\'u',
          forgotPassword: 'Haluha seña?',
          cancel: 'Kansela',
          linkIntro:
            'Hakerek ita-boot nia email atu ami haruka link ba asesu ho seguru.',
          passkeyIntro: 'Uza passkey atu asesu lalais no seguru.',
          passkeyInfoTitle: 'Kona-ba passkey',
          passkeyInfoBody:
            'Uza passkey ne\'ebé rai iha navegador ka aparelhu ida-ne\'e. Ita-boot bele aumenta husi pájina konta depois asesu tuir dalan seluk.',
          emailSentTitle: 'Email haruka ona',
          emailSentBody: 'Hare inbox hodi asesu link. Karik presiza minutu balun.',
          signIn: 'Tama',
          sendLoginLink: 'Haruka kódigu',
          loginCodeLabel: 'Kódigu',
          continuePasskey: 'Tama ho passkey',
          verifyAndSignIn: 'Verifika no asesu',
          createAccount: 'Kria konta',
          signingIn: 'Tama hela…',
          sending: 'Haruka hela…',
          verifying: 'Verifika hela…',
          signingUp: 'Rejistu hela…',
          passkeyUnavailable: 'La suporta iha navegador ida-ne\'e',
          differentSignInMethod: 'Uza métodu asesu seluk',
          mfaPasskeyRequiredTitle: 'Presiza passkey atu remata asesu',
          mfaPasskeyRequiredBody:
            'Ita-boot nia konta uza passkey hanesan pasu adisionál. Klik "Asesu ho passkey" atu verifika no remata asesu.',
          mfaRequiredTitle: 'Verifikasaun adisionál presiza',
          mfaOtherFactorBody:
            'Uza ita-boot nia authenticator (TOTP) ka kódigu recuperasaun husi aplikasaun ne\'ebé suporta.',
        },
        signInForm: {
          emailLabel: 'Email',
          emailPlaceholder: "Hatama email",
          emailInvalid: 'Hatama enderesu email ne\'ebé válidu.',
          credentialsInvalid:
            'Enderesu email no/ka seña ne\'ebé ita-boot hatama la loos.',
          codeInvalid: 'Kódigu la loos.',
          codeExpired:
            'Kódigu la válidu ka expira ona. Koko husu link asesu foun.',
          passwordLabel: 'Seña',
          passwordPlaceholder: 'Hatama ita-boot nia seña',
        },
        signupForm: {
          title: 'Kria konta',
          emailLabel: 'Email',
          emailPlaceholder: "Hatama email",
          firstNameLabel: 'Naran dahuluk',
          firstNamePlaceholder: 'Hatama naran dahuluk',
          lastNameLabel: 'Naran ikus',
          lastNamePlaceholder: 'Hatama naran ikus',
          phoneLabel: 'Númeru telefone',
          phonePlaceholder: '+670 XXXX XXXX',
          introductionLabel: 'Introdusaun',
          introductionPlaceholder: 'Introdus kona-ba ita-boot nia an...',
          optional: 'Opsionál',
          submit: 'Kria konta',
          successMessage: 'Hare ita-boot nia email atu konfirma ita-boot nia konta.',
        },
        passwordReset: {
          requestTitle: 'Haluha seña',
          requestSubtitle:
            'Hakerek ita-boot nia email atu ami haruka link ba reset ita-boot nia seña.',
          emailLabel: 'Hela eletróniku',
          emailPlaceholder: "itabot{'@'}email.com",
          sendLink: 'Haruka link ba reset',
          sending: 'Haruka hela…',
          backToSignIn: 'Fila ba tama',
          requestSuccessTitle: 'Hare ita-boot nia email',
          requestSuccessBody:
            'Karik iha konta ba enderesu ida-ne\'e, ami haruka ona instrusaun atu fila fali seña.',
          confirmTitle: 'Hili seña foun',
          confirmSubtitle: 'Hakerek seña foun ba ita-boot nia konta.',
          confirmLoading: 'Verifika link ba reset…',
          newPasswordLabel: 'Seña foun',
          confirmPasswordLabel: 'Konfirma seña foun',
          setNewPassword: 'Reset seña',
          resetting: 'Salva hela…',
          passwordsDoNotMatch: 'Seña rua ne\'e la hanesan.',
          invalidOrExpiredLink: 'Link ida-ne\'e la válidu ka expira ona.',
          showPassword: 'Hatudu seña',
          hidePassword: 'Subar seña',
        },
        verifyEmail: {
          title: 'Verifika ita-boot nia email',
          subtitle: 'Klik iha link iha ita-boot nia inbox, ka haruka fali mensajen konfirmasaun.',
          confirmPrompt: 'Klik iha botão iha kraik atu konfirma ita-boot nia enderesu email.',
          confirmButton: 'Konfirma enderesu email',
          confirming: 'Konfirma enderesu email…',
          invalidLink: 'Link verifikasaun la válidu.',
          invalidOrExpiredKey: 'Link verifikasaun la válidu ka expira ona.',
          resendButton: 'Haruka fali email verifikasaun',
          resending: 'Haruka hela…',
          resendSuccessTitle: 'Hare ita-boot nia email',
          resendSuccessBody:
            'Se iha sesaun husi rejistu iha navegador ida-ne\'e, ami haruka fali mensajen seluk. Mensajen tuan sei inkui nafatin link validu.',
          resendUnavailableTitle: 'La bele haruka fali husi navegador ida-ne\'e',
          resendUnavailableBody:
            'Uza navegador hanesan iha rejistu, ka asesu ho ita-boot nia email atu hetan link foun.',
          continueHome: 'Kontinua ba pájina inisiu',
          returnHome: 'Fila ba pájina inisiu',
          backToSignup: 'Fila ba rejistu',
          goToSignIn: 'Ba asesu',
          alreadyVerified: 'Ita-boot nia email verifika ona. Asesu ho ita-boot nia konta.',
          resendNetworkError: 'La bele haruka fali agora. Halo favor tenta tan bainhira.',
        },
        dashboard: {
          myAccount: 'Ha\'u nia konta',
          subtitle: 'Jere INDMO nia konfigurasaun no preferensia.',
          logout: 'Logout',
          accountDetails: 'Detallu konta',
          password: 'Seña',
          passkeys: 'Passkey',
          backToHome: '← Fila ba pájina inisiu',
          detailsTitle: 'Detallu konta',
          firstName: 'Naran dahuluk',
          lastName: 'Naran ikus',
          emailAddress: 'Hela eletróniku',
          phoneNumber: 'Numeru telefone',
          associatedCentres: 'Sentru treinamentu ne\'ebé asosia',
          passwordSecureHint:
            'Seña defini ona no seguru. Klik "Troka seña" hodi atualiza.',
          passwordAddHint:
            'Seidauk iha seña. Klik "Aumenta seña" hodi kria ida.',
          changePassword: 'Troka seña',
          addPassword: 'Aumenta seña',
          currentPassword: 'Seña atuál',
          passwordChangeCurrentRequired:
            'Favor hakerek ita-boot nia seña atuál.',
          confirmPassword: 'Konfirma seña',
          setPassword: 'Defini seña',
          passwordSubmitLoading: 'Atualiza hela…',
          passwordUpdatedSuccess: 'Seña atualiza ona ho susesu.',
          passwordTooShort:
            'Seña ida-ne\'e badak demais. Nia karakter mais ou menus tenki too {minLength}.',
          passkeysSubtitle: 'Asesu seguru la uza seña',
          addPasskey: 'Aumenta passkey',
          passkeyNameLabel: 'Naran passkey',
          loadingSession: 'Karega sesaun…',
          passkeysEmpty:
            'Seidauk iha passkey. Aumenta ida atu asesu lalais no seguru.',
          passkeysManageHint:
            'Aumenta passkey atu asesu la presiza seña. Ita-boot presiza email verifika iha konta.',
          passkeyRemoveAriaLabel: 'Hasai passkey',
          passkeyRenameAriaLabel: 'Troka naran passkey {name}',
          passkeyRenameSaveAriaLabel: 'Rai naran foun ba passkey {name}',
          passkeyRenameCancelAriaLabel: 'Kansela troka naran passkey {name}',
          passkeyReauthSecurityMessage:
            'Ba seguransa, konfirma ho ita-boot nia seña konta ka kódigu email iha kraik, depois kontinua aumenta passkey.',
          passkeyReauthSessionAgainMessage:
            'Ita-boot nia sesaun presiza konfirmasaun tan. Uza ita-boot nia seña ka kódigu email iha kraik atu kontinua.',
          passkeyReauthTitle:
            'Konfirma ita-boot mak hanesan atu kontinua aumenta passkey ida-ne\'e.',
          passkeyReauthAccountPassword: 'Seña konta',
          passkeyReauthOr: 'ka',
          passkeyReauthEmailHint:
            'Ami bele haruka kódigu ida-tempu tan ba enderesu email iha ita-boot nia konta.',
          passkeyReauthEmailCodeButton: 'Haruka kódigu konfirmasaun ba email',
          passkeyReauthEmailCodeButtonSending: 'Haruka hela…',
          passkeyReauthWorking: 'Serbisu hela…',
          passkeyReauthConfirmPasswordButton: 'Konfirma seña no kontinua',
          passkeyReauthVerifyCodeButton: 'Verifika kódigu no kontinua',
          passkeyReauthCodeLabel: 'Kódigo',
          passkeyReauthEmailSentNoAddress: 'Hare ita-boot nia email ba kódigu ida.',
          passkeyReauthEmailSentWithAddress:
            'Hare ita-boot nia email ba kódigu iha {email}.',
          passkeyReauthIncorrectPassword: 'Seña sala.',
          passkeyReauthPasswordConfirmFailed:
            'La bele konfirma seña ({status}).',
          passkeyReauthSignInAgain: 'Asesu fali, depois tenta aumenta passkey.',
          passkeyReauthSendCodeFailed: 'La bele haruka kódigu ({status}).',
          passkeyReauthVerifyFailed: 'Verifikasaun falla ({status}).',
          passkeyReauthGenericError: 'La bele remata. Favor tenta fali.',
          emailVerifiedSuccess:
            'Ita-boot nia email verifika ona. INDMO nia funsionário sira sei revista ita-boot nia konta molok aprova.',
          dismissNotification: 'Taka avizu',
          approvalPendingBanner:
            'Ita-boot nia konta seidauk aprova. Funsionalidade balun bele limitadu to\'o INDMO nia funsionário sira aprova rejistu.',
          cmsAccessDeniedBanner:
            'Ita-boot nia konta la iha asesu ba CMS. Karik presiza asesu, kontaktu administradór.',
          approvalStatusLabel: 'Aprovasaun konta',
          approvalStatusApproved: 'Aprova ona',
          approvalStatusPending: 'Seidauk aprova',
          mySubmissions: 'Ha\'u nia aplikasaun sira',
          mySubmissionsTitle: 'Aplikasaun no prosesu sira',
          mySubmissionsLoading: 'Karega…',
          mySubmissionsError: 'La bele karega lista agora.',
          mySubmissionsEmpty: 'Seidauk iha aplikasaun ka enviu iha rai-laran.',
          mySubmissionsColType: 'Tipu',
          mySubmissionsColSummary: 'Rezumu',
          mySubmissionsColStatus: 'Estadu',
          mySubmissionsColSubmitted: 'Submete',
          mySubmissionsColTask: 'Etapa atuál sira',
          mySubmissionsWorkflowInProgress: 'Prosesu hela',
          mySubmissionsStaffComments: 'Komentáriu husi funsionário',
          mySubmissionsStaffCommentsAria: 'Komentáriu husi funsionário ({count})',
          mySubmissionsWorkflowMessages: 'Fluxu servisu mensajen sira',
          mySubmissionsMessageRoleStaff: 'Funsionário',
          mySubmissionsMessageRoleYou: "Ita-boot",
          mySubmissionsReplyLabel: "Ita-boot nia mensajen ba INDMO",
          mySubmissionsReplyPlaceholder: "Hakerek mensajen ida…",
          mySubmissionsReplySubmit: "Haruka",
          mySubmissionsReplySending: "Haruka hela…",
          mySubmissionsReplyError: "La bele haruka. Favor tenta fali.",
          submissionStatus: {
            submitted: 'Submete ona',
            completed: 'Remata ona',
            rejected: 'Rijeita',
            pending_offline: 'Hela prosesu offline',
          },
          workflowPushTitle: 'Notifikasaun traballu (browser)',
          workflowPushHint:
            'Simu avizu iha aparelhu ida-ne\'ebé bainhira iha tarefa moderasaun ka prosesu.',
          workflowPushEnable: 'Liga notifikasaun browser',
          workflowPushEnabled: 'Notifikasaun browser liga ona.',
          workflowPushUnavailable: 'Push la disponível iha konfigurasaun servidor.',
          workflowPushError: 'La bele liga notifikasaun. Favor tenta fali.',
          workflowPushNeedsSw:
            'Notifikasaun browser presiza build produsaun (service worker). Uza npm run build no preview, ka buka iha HTTPS.',
          needHelpPushPrompt: 'Halo favor informa ha\'u bainhira iha resposta.',
          needHelpPushButton: 'Liga notifikasaun',
          needHelpPushDone: 'Ita-boot sei hetan avizu iha aparelhu ida-ne\'e.',
          helpQueryPortalThankYouLink: 'Haree ita-boot nia pedidu no mensajen sira',
          helpQueryPortalThankYouHint:
            'Ita-boot nia ligasaun seguru rai iha aparelhu ida-ne\'e. Ita-boot mós bele marka favoritu ka kopia ligasaun husi barra enderesu nia navegadór.',
          helpQueryStatusTitle: 'Ha\'u nia pedidu ajuda',
          helpQueryStatusIntro:
            'Ita-boot bele kontinua uza situs INDMO. Bainhira ita-boot liga notifikasaun, sei hatudu avizu iha aparelhu ida-ne\'e.',
          helpQueryStatusInvalidLink:
            'Ligasaun la válidu ka expira ona. Huwi ba pájina prinsipál ka rejuviu pedidu ajuda.',
          helpQueryStatusLoadError: 'La bele karrega pedidu ida-ne\'e. Favor tenta fali.',
          helpQueryStatusBackHome: 'Fila ba pájina prinsipál',
          helpQueryStatusNoMessagesYet: 'Seidauk iha mensajen iha ne\'ebá.',
        },
      },
      siteUnderConstruction: {
        title: 'Sit iha konstrusaun',
        body:
          'Pájina públiku la disponível hela. Administradór sira bele asesu konta nian ka admin CMS.',
        accountLink: 'Login / Ha\'u nia konta',
        adminLink: 'Admin CMS (Wagtail)',
      },
      sideNav: {
        howCanWeHelp: 'Oinsá ami bele ajuda?',
        followUs: 'Tuir ami',
        megaSectionIntro: 'Eksplora seksaun ida-ne\'e',
      },
      partnersCarousel: {
        title: 'Ami-nia Parseiru sira',
        scrollLeft: 'Scroll parseiru ba karuk',
        scrollRight: 'Scroll parseiru ba oin',
      },
      footer: {
        quickLinks: 'Link lalais',
        resources: 'Rekursu sira',
        contactUs: 'Kontaktu',
        legalNav: 'Ligasaun legál',
        buildRelease: 'Lansamentu: {tag}',
        buildTime: 'Tempu build: {time}',
        buildGit: 'Commit: {sha}',
        buildInfoUnavailable: 'Metadata build la iha (dev lokál ka build antigu).',
      },
      processFlow: {
        back: 'Fila',
        next: 'Tuir mai',
        stepOf: 'Pasu {current} husi {total}',
        submitApplication: 'Submete aplikasaun',
        submitting: 'Submete hela…',
        close: 'Taka',
      },
      news: {
        featured: 'Destakadú',
        readingMinutes: 'Lee min {n}',
        readMore: 'Lee tan',
        showingRange:
          'Hatudu {from}–{to} husi {total} notisia',
        backToLatestNews: 'Fila ba notisia foin',
        tagsHeading: 'Etiketa:',
        shareHeading: 'Fahe notisia ida-ne\'e:',
        shareRegionAria: 'Fahe notisia',
        shareCopyLink: 'Kopia link',
        shareFacebook: 'Fahe iha Facebook',
        shareCopied: 'Ligasaun kopia ona.',
        byline: 'Husi {name}',
        previous: 'Fila',
        next: 'Tuir mai',
        pageOf: 'Pájina {current} husi {total}',
        findNews: 'Buka notísia',
        searchLabel: 'Buka',
        searchPlaceholder: 'Buka notísia tuir títulu ka konteúdu…',
        category: 'Kategoria',
        filterAll: 'Hotu-hotu',
      },
      form: {
        filesLabel: 'Faila',
        fileLabel: 'Faila',
        fileRequired: 'Tau faila ida molok submete.',
        filesRequired: 'Tau pelume faila molok submete.',
        correctErrorsBelow: 'Favor korrigi sala iha okos.',
        uploadAriaLabel: 'Tau faila',
        uploadOneFileAriaLabel: 'Tau faila ida',
        clickToUpload: 'Klik atu tau',
        orDragAndDrop: ' ka laran no solta',
        uploadFileTypesHint: 'De\'it faila {types}',
        maxHint: ' (máks. {max})',
        filesSelectedCount: '{count} faila hili:',
        removeFileAriaLabel: 'Hasai faila',
        thankYou: 'Obrigado ba ita-boot nia submisaun.',
        submit: 'Submete',
        submitting: 'Submete hela…',
      },
      trainingCentreList: {
        sectionAria: 'Lista organizasaun sira',
        filterHeading: 'Buka Sentru Formasaun sira',
        search: 'Buka',
        searchPlaceholder: 'Buka tuktu naran…',
        district: 'Munisípiu',
        subdistrict: 'Postu Administrativu',
        allSubdistricts: 'Postu Administrativu hotu-hotu',
        level: 'Nível',
        all: 'Hotu-hotu',
        courseCountOne: '1 Kursu',
        courseCountMany: '{count} Kursu',
        showingCount: 'Hatudu {shown} husi {total} sentru',
        viewDetails: 'Hare detallu',
        directoryMapTitle: 'Mapa sentru sira hotu',
        directoryMapSummaryAria: 'Mapa ne\'ebé bele halo tuir no hatudu lokalizasaun sentru sira iha diretóriu nian',
        directoryMapCanvasAria: 'Mapa hatudu lokalizasaun sentru treinamentu sira',
        noResultsTitle: 'Laiha sentru',
        noResultsHint: 'Koko filtru sira',
        noTrainingCentres: 'Laiha organizasaun iha lista.',
        loading: 'Karregamentu…',
        loadError: 'La konsege load lista.',
        programsOfferedAria: 'Programa sira ne\'ebé oferese',
      },
      programList: {
        sectionAria: 'Lista programa akreditadu sira',
        filterHeading: 'Buka programa akreditadu sira',
        search: 'Buka',
        searchPlaceholder: 'Buka tuktu naran ka kodigu…',
        offeredIn: 'Ofertadu iha',
        district: 'Munisípiu',
        subdistrict: 'Postu Administrativu',
        allSubdistricts: 'Postu Administrativu hotu-hotu',
        level: 'Nível programa',
        all: 'Hotu-hotu',
        showingCount: 'Hatudu {shown} husi {total} programa',
        viewDetails: 'Hare detallu',
        noResultsTitle: 'Laiha programa',
        noResultsHint: 'Koko filtru sira',
        noPrograms: 'Laiha programa iha lista.',
        loading: 'Karregamentu…',
        loadError: 'La konsege load lista.',
        centresOfferingAria: 'Sentru treinamentu sira',
        trainingCentresCountOne: '1 Sentru treinamentu',
        trainingCentresCountMany: '{count} Sentru treinamentu',
        directoryMapTitle: 'Mapa sentru treinamentu',
        directoryMapSummaryAria:
          'Mapa ne\'ebé bele halo tuir ho lokalizasaun sentru sira ne\'ebé oferese programa iha filtru atual',
        directoryMapCanvasAria: 'Mapa hatudu lokalizasaun sentru treinamentu ba programa ne\'ebé iha filtru',
      },
      programCertificateTier: {
        I: 'Sertifikadu I',
        II: 'Sertifikadu II',
        III: 'Sertifikadu III',
        IV: 'Sertifikadu IV',
      },
      programAccreditationClassification: 'Klasifikasaun {letter}',
      trainingCentreDetail: {
        summaryAria: 'Rezumu organizasaun',
        code: 'Kódigu',
        accreditation: 'Akreditasaun',
        locations: 'Lokalizasaun',
        programsOffered: 'Programa sira ne\'ebé oferese',
        programCode: 'Kódigu programa',
        accreditationNoProgram: 'Númeru akreditasaun programa',
        accreditationNo: 'Númeru akreditasaun',
        validFrom: 'Hahú',
        validTo: 'Remata',
        comment: 'Komentáriu',
        notListed: 'Organizasaun ida-ne\'e la iha diretóriu públiku nian.',
        loadError: 'La konsege load detallu organizasaun nian.',
        unavailableOffline: 'Presiza ligasaun internet atu hatama informasaun tan.',
        loading: 'Karregamentu…',
        backToDirectory: 'Fila ba lista sentru treinamentu',
        aboutThisCentre: 'Kona-ba sentru ida-ne\'e',
        contactInformation: 'Informasaun kontaktu',
        locationMapTitle: 'Mapa lokalizasaun',
        mapPinFallback: 'Lokal',
        addressLabel: 'Enderesu',
        centreStatistics: 'Estatístika sentru',
        programsStatLabel: 'Programa',
        certificateLevelsStatLabel: 'Nível sertifikadu',
        searchProgramsPlaceholder: 'Buka programa…',
        allStatuses: 'Status hotu-hotu',
        filterStatusAccredited: 'Akreditadu',
        filterStatusNonAccredited: 'La-akreditadu',
        allLevels: 'Nível hotu-hotu',
        allDates: 'Data hotu-hotu',
        filterDatesActive: 'Ativu agora',
        filterDatesExpiring: 'Remata besik',
        loadMorePrograms: 'Karega programa tan',
        programsCountOne: '1 programa',
        programsCountMany: '{count} programa',
        noProgramsFilteredTitle: 'Laiha programa',
        noProgramsFilteredHint: 'Koko filtru sira',
        accreditationFieldLabel: 'Akreditasaun',
        datesFieldLabel: 'Data',
        centreNotFoundTitle: 'Sentru la hetan',
        centreNotFoundBody: 'Sentru ne\'ebé ita buka la iha ka hasai ona.',
        programChipAccredited: 'Akreditadu',
        programChipLicensed: 'Lisensiadu',
        programChipNonAccredited: 'La-akreditadu',
        certificateLevelRangeNa: 'N/A',
      },
      programDetail: {
        summaryAria: 'Rezumu programa',
        code: 'Kódigu',
        certificateLevel: 'Nível sertifikadu',
        trainingCentres: 'Sentru treinamentu sira',
        noCentres: 'Laiha sentru treinamentu lista husi diretóriu.',
        notFound: 'Programa la hetan.',
        notFoundBody: 'Programa ne\'ebé ita buka la iha ka hasai ona.',
        loadError: 'La konsege load detallu programa nian.',
        unavailableOffline: 'Presiza ligasaun internet atu hatama detallu programa nian.',
        loading: 'Karregamentu…',
        backToPrograms: 'Fila ba lista programa',
        aboutThisProgram: 'Kona-ba programa ida-ne\'e',
        centresOfferingHeading: 'Sentru treinamentu sira ne\'ebé oferese programa ida-ne\'e',
        searchCentresPlaceholder: 'Buka sentru…',
        allMunicipalities: 'Munisípiu hotu-hotu',
        centresListedCountOne: '1 sentru',
        centresListedCountMany: '{count} sentru',
        loadMoreCentres: 'Karega sentru tan',
        noCentresFilteredTitle: 'Laiha sentru',
        noCentresFilteredHint: 'Koko filtru sira',
        directoryMapTitle: 'Mapa sentru sira',
        directoryMapSummaryAria:
          'Mapa ne\'ebé bele halo tuir ho lokalizasaun sentru sira ne\'ebé oferese programa ida-ne\'e',
        directoryMapCanvasAria: 'Mapa hatudu lokalizasaun sentru treinamentu ba programa ida-ne\'e',
        competenciesHeading: 'Kompeténsia sira',
        noCompetencies: 'Laiha kompeténsia lista ba programa ida-ne\'e.',
        competencyHours: '{hours} oras',
      },
      certificateVerify: {
        title: 'Verifika sertifikadu',
        subtitle: 'Hare se sertifikadu ne\'e rejistu iha INDMO.',
        validating: 'Verifika hela…',
        successTitle: 'Sertifikadu verifika ho susesu',
        successSubtitle: 'Sertifikadu ne\'e auténtiku no INDMO nian.',
        detailsHeading: 'Detallu sertifikadu',
        nameLabel: 'Naran',
        dateIssued: 'Data emissaun',
        program: 'Programa',
        trainingCentre: 'Sentru treinamentu',
        invalidMessage: 'Link ida-ne\'e la válidu ka sertifikadu la hetan.',
        networkError: 'La konsege konekta. Halo favor tenta tan.',
        returnHome: 'Fila ba pájina inisiu',
      },
      blocks: {
        authorisationRequired: {
          createAccount: 'Kria konta',
          goToSubmissions: 'Ba ha\'u nia aplikasaun no submisaun',
          goToAccount: 'Ba ha\'u nia konta',
        },
      },
    },
    en: {
      common: {
        connectionRequired: 'Connection required.',
        offlineMutationsHint:
          'Actions that contact the server stay disabled until you’re back online.',
      },
      nav: {
        language: 'Language:',
        selectLanguage: 'Select language',
        changeLanguage: 'Change language',
        search: 'Search…',
        searchNoResults: 'No matching pages',
        searchLoading: 'Searching…',
        searchClear: 'Clear search',
        searchResultsList: 'Search results',
        searchGoToLocale: 'Go to {locale} version',
        login: 'Login',
        loginAccount: 'Login / My Account',
        localeName: {
          tet: 'Tetum',
          en: 'English',
          pt: 'Portuguese',
        },
        contentLocaleMismatchHint: 'Page in {locale}',
        contentLocaleMismatchTitle:
          'Page content is in {content}. Menus and labels use {ui}.',
      },
      account: {
        none: 'None',
        loginTitleFallback: 'Welcome to INDMO',
        loginMessageFallback:
          'Access your account to manage certifications, view training programs, and connect with Timor-Leste\'s vocational education network.',
        loginImageAlt: 'INDMO vocational training',
        savedHelpQueriesTitle: 'Your help requests on this device',
        savedHelpQueriesHint:
          'Saved when you submitted without signing in. Open a request to view messages or reply.',
        savedHelpQueriesLoading: 'Loading…',
        webauthn: {
          notAllowed: 'Sign-in was cancelled or not allowed by your device.',
          requestFailed: 'WebAuthn request failed.',
          genericError: 'Something went wrong.',
        },
        authCard: {
          signInTitle: 'Sign In',
          createTitle: 'Create Account',
          signInSubtitle: 'Access your INDMO account',
          createSubtitle: 'Join the INDMO community',
          tabPassword: 'Email & Password',
          tabLink: 'Email Code',
          tabPasskey: 'Passkey',
          tabCreate: 'Create Account',
          termsSignIn:
            'By signing in, you agree to INDMO\'s terms of service and privacy policy.',
          termsCreate:
            'By creating an account, you agree to INDMO\'s terms of service and privacy policy.',
          backToHome: '← Back to Home',
          rememberMe: 'Remember me',
          forgotPassword: 'Forgot password?',
          cancel: 'Cancel',
          linkIntro:
            'Enter your email address and we\'ll send you a secure login link.',
          passkeyIntro:
            'Use a passkey for quick and secure authentication.',
          passkeyInfoTitle: 'About passkeys',
          passkeyInfoBody:
            'Use a passkey saved to this browser or device. You can add one from your account page after signing in another way.',
          emailSentTitle: 'Email sent',
          emailSentBody: 'Check your inbox for a login link. It may take a minute to arrive.',
          signIn: 'Sign In',
          sendLoginLink: 'Send login code',
          loginCodeLabel: 'Code',
          continuePasskey: 'Sign in with passkey',
          verifyAndSignIn: 'Verify and sign in',
          createAccount: 'Create Account',
          signingIn: 'Signing in…',
          sending: 'Sending…',
          verifying: 'Verifying…',
          signingUp: 'Signing up…',
          passkeyUnavailable: 'Not supported in this browser',
          differentSignInMethod: 'Use a different sign-in method',
          mfaPasskeyRequiredTitle: 'Passkey required to finish signing in',
          mfaPasskeyRequiredBody:
            'Your password was accepted. Use your passkey to complete the second step of sign-in.',
          mfaRequiredTitle: 'Additional verification required',
          mfaOtherFactorBody:
            'Complete sign-in with your authenticator app or a recovery code where your account settings allow.',
        },
        signInForm: {
          emailLabel: 'Email',
          emailPlaceholder: "your.email{'@'}example.com",
          emailInvalid: 'Enter a valid email address.',
          credentialsInvalid:
            'The email address and/or password you specified are not correct.',
          codeInvalid: 'Incorrect code.',
          codeExpired:
            'The code is invalid or has expired. Try requesting a new login link.',
          passwordLabel: 'Password',
          passwordPlaceholder: 'Enter your password',
        },
        signupForm: {
          title: 'Create an account',
          emailLabel: 'Email',
          emailPlaceholder: "your.email{'@'}example.com",
          firstNameLabel: 'First name',
          firstNamePlaceholder: 'Enter first name',
          lastNameLabel: 'Last name',
          lastNamePlaceholder: 'Enter last name',
          phoneLabel: 'Phone number',
          phonePlaceholder: '+670 XXXX XXXX',
          introductionLabel: 'Introduction',
          introductionPlaceholder: 'Tell us about yourself...',
          optional: 'Optional',
          submit: 'Sign up',
          successMessage: 'Check your email to confirm your account.',
        },
        passwordReset: {
          requestTitle: 'Forgot password',
          requestSubtitle:
            'Enter your email address and we will send you a link to reset your password.',
          emailLabel: 'Email',
          emailPlaceholder: "your.email{'@'}example.com",
          sendLink: 'Send reset link',
          sending: 'Sending…',
          backToSignIn: 'Back to sign in',
          requestSuccessTitle: 'Check your email',
          requestSuccessBody:
            'If an account exists for that address, we have sent password reset instructions.',
          confirmTitle: 'Choose a new password',
          confirmSubtitle: 'Enter a new password for your account.',
          confirmLoading: 'Checking your reset link…',
          newPasswordLabel: 'New password',
          confirmPasswordLabel: 'Confirm new passworid',
          setNewPassword: 'Reset password',
          resetting: 'Saving…',
          passwordsDoNotMatch: 'The passwords you entered do not match.',
          invalidOrExpiredLink: 'This reset link is invalid or has expired.',
          showPassword: 'Show password',
          hidePassword: 'Hide password',
        },
        verifyEmail: {
          title: 'Verify your email',
          subtitle: 'Use the link we sent, or request another confirmation email.',
          confirmPrompt: 'Click the button below to confirm your email address.',
          confirmButton: 'Confirm email address',
          confirming: 'Confirming your email address…',
          invalidLink: 'This verification link is invalid.',
          invalidOrExpiredKey: 'This verification link is invalid or has expired.',
          resendButton: 'Resend verification email',
          resending: 'Sending…',
          resendSuccessTitle: 'Check your email',
          resendSuccessBody:
            'If this browser still has your sign-up session, we sent another message. Older messages may still contain a valid link.',
          resendUnavailableTitle: 'We could not resend from this browser',
          resendUnavailableBody:
            'Use the same browser where you signed up, or sign in with your email to get a new link.',
          continueHome: 'Continue to home',
          returnHome: 'Return to home',
          backToSignup: 'Back to sign up',
          goToSignIn: 'Go to sign in',
          alreadyVerified: 'Your email is already verified. You can sign in to your account.',
          resendNetworkError: 'Could not send right now. Please try again in a moment.',
        },
        dashboard: {
          myAccount: 'My Account',
          subtitle: 'Manage your INDMO account settings and preferences',
          logout: 'Logout',
          accountDetails: 'Account Details',
          password: 'Password',
          passkeys: 'Passkeys',
          backToHome: '← Back to Home',
          detailsTitle: 'Account Details',
          firstName: 'First Name',
          lastName: 'Last Name',
          emailAddress: 'Email Address',
          phoneNumber: 'Phone Number',
          associatedCentres: 'Associated Training Centres',
          passwordSecureHint:
            'Password is set and secure. Click "Change Password" to update it.',
          passwordAddHint:
            'No password is set yet. Click "Add Password" to create one.',
          changePassword: 'Change Password',
          addPassword: 'Add Password',
          currentPassword: 'Current password',
          passwordChangeCurrentRequired: 'Please type your current password.',
          confirmPassword: 'Confirm password',
          setPassword: 'Set password',
          passwordSubmitLoading: 'Updating…',
          passwordUpdatedSuccess: 'Password updated successfully.',
          passwordTooShort:
            'This password is too short. It must contain at least {minLength} characters.',
          passkeysSubtitle: 'Secure, passwordless authentication',
          addPasskey: 'Add passkey',
          passkeyNameLabel: 'Passkey name',
          loadingSession: 'Loading session…',
          passkeysEmpty:
            'No passkeys added yet. Add one for quick and secure access.',
          passkeysManageHint:
            'Add a passkey to sign in without a password. You need a verified email on your account.',
          passkeyRemoveAriaLabel: 'Remove passkey',
          passkeyRenameAriaLabel: 'Rename passkey {name}',
          passkeyRenameSaveAriaLabel: 'Save new name for passkey {name}',
          passkeyRenameCancelAriaLabel: 'Cancel renaming passkey {name}',
          passkeyReauthSecurityMessage:
            'For your security, confirm with your account password or an email code below, then continue adding your passkey.',
          passkeyReauthSessionAgainMessage:
            'Your session requires confirmation again. Use your password or an email code below to continue.',
          passkeyReauthTitle: "Confirm it's you to continue adding this passkey.",
          passkeyReauthAccountPassword: 'Account password',
          passkeyReauthOr: 'or',
          passkeyReauthEmailHint:
            'We can email you a one-time code to the address on your account.',
          passkeyReauthEmailCodeButton: 'Email me a confirmation code',
          passkeyReauthEmailCodeButtonSending: 'Sending…',
          passkeyReauthWorking: 'Working…',
          passkeyReauthConfirmPasswordButton: 'Confirm password and continue',
          passkeyReauthVerifyCodeButton: 'Verify code and continue',
          passkeyReauthCodeLabel: 'Code',
          passkeyReauthEmailSentNoAddress: 'Check your email for a code.',
          passkeyReauthEmailSentWithAddress:
            'Check your email for a code at {email}.',
          passkeyReauthIncorrectPassword: 'Incorrect password.',
          passkeyReauthPasswordConfirmFailed:
            'Could not confirm password ({status}).',
          passkeyReauthSignInAgain: 'Sign in again, then try adding your passkey.',
          passkeyReauthSendCodeFailed: 'Could not send code ({status}).',
          passkeyReauthVerifyFailed: 'Verification failed ({status}).',
          passkeyReauthGenericError: 'Something went wrong.',
          emailVerifiedSuccess:
            'Your email has been verified. Your account will be reviewed by INDMO staff before being approved.',
          dismissNotification: 'Dismiss notification',
          approvalPendingBanner:
            'Your account is not approved yet. Some parts of the site stay limited until INDMO staff approve your registration.',
          cmsAccessDeniedBanner:
            'This account does not have access to the CMS. If you need access, contact an administrator.',
          approvalStatusLabel: 'Account approval',
          approvalStatusApproved: 'Approved',
          approvalStatusPending: 'Pending approval',
          mySubmissions: 'My submissions',
          mySubmissionsTitle: 'Applications and processes',
          mySubmissionsLoading: 'Loading…',
          mySubmissionsError: 'Could not load this list right now.',
          mySubmissionsEmpty: 'You have no applications or online submissions yet.',
          mySubmissionsColType: 'Type',
          mySubmissionsColSummary: 'Summary',
          mySubmissionsColStatus: 'Status',
          mySubmissionsColSubmitted: 'Submitted',
          mySubmissionsColTask: 'Current step',
          mySubmissionsWorkflowInProgress: 'In progress',
          mySubmissionsStaffComments: 'Staff messages',
          mySubmissionsStaffCommentsAria: 'Staff messages ({count})',
          mySubmissionsWorkflowMessages: 'Workflow messages',
          mySubmissionsMessageRoleStaff: 'Staff',
          mySubmissionsMessageRoleYou: 'You',
          mySubmissionsReplyLabel: 'Your message to INDMO',
          mySubmissionsReplyPlaceholder: 'Write a message…',
          mySubmissionsReplySubmit: 'Send',
          mySubmissionsReplySending: 'Sending…',
          mySubmissionsReplyError: 'Could not send. Please try again.',
          submissionStatus: {
            submitted: 'Submitted',
            completed: 'Completed',
            rejected: 'Rejected',
            pending_offline: 'Pending offline process',
          },
          workflowPushTitle: 'Workflow notifications (browser)',
          workflowPushHint:
            'Get alerts on this device when there are moderation tasks or process updates for your account.',
          workflowPushEnable: 'Turn on browser notifications',
          workflowPushEnabled: 'Browser notifications are enabled.',
          workflowPushUnavailable: 'Push is not configured on the server.',
          workflowPushError: 'Could not enable notifications. Please try again.',
          workflowPushNeedsSw:
            'Browser notifications need a production build with a service worker (e.g. npm run build && vite preview) over HTTPS.',
          needHelpPushPrompt: 'Want to be notified when we reply?',
          needHelpPushButton: 'Turn on notifications',
          needHelpPushDone: 'You will get alerts on this device when we respond.',
          helpQueryPortalThankYouLink: 'View your request and messages',
          helpQueryPortalThankYouHint:
            'A secure access link is saved in this browser. You can also bookmark or copy the address from the bar for use elsewhere.',
          helpQueryStatusTitle: 'Your help request',
          helpQueryStatusIntro:
            'You can keep using INDMO. If you enabled notifications, we will alert this device when there are updates.',
          helpQueryStatusInvalidLink:
            'This link is invalid or has expired. Go back to the home page or submit a new help request.',
          helpQueryStatusLoadError: 'Could not load this request. Please try again.',
          helpQueryStatusBackHome: 'Back to home',
          helpQueryStatusNoMessagesYet: 'No messages in this thread yet.',
        },
      },
      siteUnderConstruction: {
        title: 'Site is under construction',
        body:
          'The public site is not available yet. Staff can sign in below or open the CMS admin.',
        accountLink: 'Login / My account',
        adminLink: 'CMS admin (Wagtail)',
      },
      sideNav: {
        howCanWeHelp: 'How can we help you?',
        followUs: 'Follow us',
        megaSectionIntro: 'Explore this section',
      },
      partnersCarousel: {
        title: 'Our Partners',
        scrollLeft: 'Scroll partners left',
        scrollRight: 'Scroll partners right',
      },
      footer: {
        quickLinks: 'Quick Links',
        resources: 'Resources',
        contactUs: 'Contact Us',
        legalNav: 'Legal links',
        buildRelease: 'Release: {tag}',
        buildTime: 'Built: {time}',
        buildGit: 'Commit: {sha}',
        buildInfoUnavailable: 'Build metadata not embedded (local dev or legacy build).',
      },
      processFlow: {
        back: 'Back',
        next: 'Next',
        stepOf: 'Step {current} of {total}',
        submitApplication: 'Submit Application',
        submitting: 'Submitting…',
        close: 'Close',
      },
      news: {
        featured: 'Featured',
        readingMinutes: '{n} min read',
        readMore: 'Read more',
        showingRange: 'Showing {from}–{to} of {total} news items',
        backToLatestNews: 'Back to Latest News',
        tagsHeading: 'Tags:',
        shareHeading: 'Share this article:',
        shareRegionAria: 'Share this article',
        shareCopyLink: 'Copy link',
        shareFacebook: 'Share on Facebook',
        shareCopied: 'Link copied to clipboard.',
        byline: 'By {name}',
        previous: 'Previous',
        next: 'Next',
        pageOf: 'Page {current} of {total}',
        findNews: 'Find News',
        searchLabel: 'Search',
        searchPlaceholder: 'Search news by title or content…',
        category: 'Category',
        filterAll: 'All',
      },
      form: {
        filesLabel: 'Files',
        fileLabel: 'File',
        fileRequired: 'Please upload a file before submitting.',
        filesRequired: 'Please upload at least one file before submitting.',
        correctErrorsBelow: 'Please correct the errors below.',
        uploadAriaLabel: 'Upload files',
        uploadOneFileAriaLabel: 'Upload file',
        clickToUpload: 'Click to upload',
        orDragAndDrop: ' or drag and drop',
        uploadFileTypesHint: '{types} files only',
        maxHint: ' (max {max})',
        filesSelectedCount: '{count} file(s) selected:',
        removeFileAriaLabel: 'Remove file',
        thankYou: 'Thank you for your submission.',
        submit: 'Submit',
        submitting: 'Submitting…',
      },
      trainingCentreList: {
        sectionAria: 'Training centre directory',
        filterHeading: 'Find Training Centers',
        search: 'Search',
        searchPlaceholder: 'Search by name…',
        district: 'District',
        subdistrict: 'Subdistrict',
        allSubdistricts: 'All subdistricts',
        level: 'Level',
        all: 'All',
        courseCountOne: '1 Course',
        courseCountMany: '{count} Courses',
        showingCount: 'Showing {shown} of {total} centres',
        viewDetails: 'View Details',
        directoryMapTitle: 'Map of all centres',
        directoryMapSummaryAria: 'Expandable map of every training centre in the directory that has a map location',
        directoryMapCanvasAria: 'Map of training centre locations',
        noResultsTitle: 'No centres found',
        noResultsHint: 'Try adjusting your search or filters',
        noTrainingCentres: 'No training centres are listed yet.',
        loading: 'Loading…',
        loadError: 'Could not load the list.',
        programsOfferedAria: 'Programs offered',
      },
      programList: {
        sectionAria: 'Accredited program directory',
        filterHeading: 'Find accredited programs',
        search: 'Search',
        searchPlaceholder: 'Search by name or code…',
        offeredIn: 'Offered in',
        district: 'District',
        subdistrict: 'Subdistrict',
        allSubdistricts: 'All subdistricts',
        level: 'Program level',
        all: 'All',
        showingCount: 'Showing {shown} of {total} programs',
        viewDetails: 'View details',
        noResultsTitle: 'No programs found',
        noResultsHint: 'Try adjusting your search or filters',
        noPrograms: 'No programs are listed yet.',
        loading: 'Loading…',
        loadError: 'Could not load the list.',
        centresOfferingAria: 'Training centres',
        trainingCentresCountOne: '1 Training Centre',
        trainingCentresCountMany: '{count} Training Centres',
        directoryMapTitle: 'Training centres map',
        directoryMapSummaryAria:
          'Expandable map of training centres that offer the programs currently shown in the list',
        directoryMapCanvasAria: 'Map of training centre locations for the filtered program list',
      },
      programCertificateTier: {
        I: 'Certificate I',
        II: 'Certificate II',
        III: 'Certificate III',
        IV: 'Certificate IV',
      },
      programAccreditationClassification: 'Classification {letter}',
      trainingCentreDetail: {
        summaryAria: 'Training centre summary',
        code: 'Code',
        accreditation: 'Accreditation',
        locations: 'Locations',
        programsOffered: 'Programs offered',
        programCode: 'Program code',
        accreditationNoProgram: 'Program accreditation number',
        accreditationNo: 'Accreditation number',
        validFrom: 'From',
        validTo: 'To',
        comment: 'Comment',
        notListed: 'This training centre is not listed in the public directory.',
        loadError: 'Could not load training centre details.',
        unavailableOffline: 'Connect to the internet to load full training centre details.',
        loading: 'Loading…',
        backToDirectory: 'Back to training centres',
        aboutThisCentre: 'About this centre',
        contactInformation: 'Contact information',
        locationMapTitle: 'Location map',
        mapPinFallback: 'Site',
        addressLabel: 'Address',
        centreStatistics: 'Centre statistics',
        programsStatLabel: 'Programs',
        certificateLevelsStatLabel: 'Certificate levels',
        searchProgramsPlaceholder: 'Search programs…',
        allStatuses: 'All statuses',
        filterStatusAccredited: 'Accredited',
        filterStatusNonAccredited: 'Non-Accredited',
        allLevels: 'All levels',
        allDates: 'All dates',
        filterDatesActive: 'Currently active',
        filterDatesExpiring: 'Expiring soon',
        loadMorePrograms: 'Load more programs',
        programsCountOne: '1 program',
        programsCountMany: '{count} programs',
        noProgramsFilteredTitle: 'No programs found',
        noProgramsFilteredHint: 'Try adjusting your filters',
        accreditationFieldLabel: 'Accreditation',
        datesFieldLabel: 'Dates',
        centreNotFoundTitle: 'Centre not found',
        centreNotFoundBody:
          "The training centre you're looking for doesn't exist or has been removed.",
        programChipAccredited: 'Accredited',
        programChipLicensed: 'Licensed',
        programChipNonAccredited: 'Non-Accredited',
        certificateLevelRangeNa: 'N/A',
      },
      programDetail: {
        summaryAria: 'Program summary',
        code: 'Code',
        certificateLevel: 'Certificate level',
        trainingCentres: 'Training centres',
        noCentres: 'No training centres are listed for this program in the directory.',
        notFound: 'Program not found.',
        notFoundBody: "The program you're looking for doesn't exist or has been removed.",
        loadError: 'Could not load program details.',
        unavailableOffline: 'Connect to the internet to load full program details.',
        loading: 'Loading…',
        backToPrograms: 'Back to programs',
        aboutThisProgram: 'About this program',
        centresOfferingHeading: 'Training centres offering this program',
        searchCentresPlaceholder: 'Search centres…',
        allMunicipalities: 'All municipalities',
        centresListedCountOne: '1 centre',
        centresListedCountMany: '{count} centres',
        loadMoreCentres: 'Load more centres',
        noCentresFilteredTitle: 'No centres found',
        noCentresFilteredHint: 'Try adjusting your filters',
        directoryMapTitle: 'Training centres map',
        directoryMapSummaryAria:
          'Expandable map of training centre locations that offer this program',
        directoryMapCanvasAria: 'Map of training centre locations for this program',
        competenciesHeading: 'Competencies',
        noCompetencies: 'No competencies are linked to this program yet.',
        competencyHours: '{hours} h',
      },
      certificateVerify: {
        title: 'Verify certificate',
        subtitle: 'Check whether this certificate is registered with INDMO.',
        validating: 'Validating…',
        successTitle: 'Certificate verified successfully',
        successSubtitle: 'This certificate is authentic and issued by INDMO.',
        detailsHeading: 'Certificate details',
        nameLabel: 'Name',
        dateIssued: 'Date issued',
        program: 'Program',
        trainingCentre: 'Training centre',
        invalidMessage: 'This link is invalid or the certificate could not be found.',
        networkError: 'Could not connect. Please try again.',
        returnHome: 'Return to home',
      },
      blocks: {
        authorisationRequired: {
          createAccount: 'Create an account',
          goToSubmissions: 'Go to my applications and submissions',
          goToAccount: 'Go to account',
        },
      },
    },
    pt: {
      common: {
        connectionRequired: 'É necessária ligação à internet.',
        offlineMutationsHint:
          'As ações que comunicam com o servidor ficam desativadas até voltar a ter ligação.',
        dismiss: 'Fechar',
      },
      nav: {
        language: 'Idioma:',
        selectLanguage: 'Selecionar idioma',
        changeLanguage: 'Alterar idioma',
        search: 'Pesquisar…',
        searchNoResults: 'Sem páginas correspondentes',
        searchLoading: 'A pesquisar…',
        searchClear: 'Limpar pesquisa',
        searchResultsList: 'Resultados da pesquisa',
        searchGoToLocale: 'Abrir versão em {locale}',
        login: 'Entrar',
        loginAccount: 'Entrar / A minha conta',
        localeName: {
          tet: 'Tétum',
          en: 'Inglês',
          pt: 'Português',
        },
        contentLocaleMismatchHint: 'Página em {locale}',
        contentLocaleMismatchTitle:
          'O conteúdo desta página está em {content}; menus e rótulos estão em {ui}.',
      },
      account: {
        none: 'Nenhum',
        loginTitleFallback: 'Bem-vindo ao INDMO',
        loginMessageFallback:
          'Aceda à sua conta para gerir certificações, ver programas de formação e ligar-se à rede de educação vocacional de Timor-Leste.',
        loginImageAlt: 'Formação vocacional INDMO',
        savedHelpQueriesTitle: 'Os seus pedidos de ajuda neste dispositivo',
        savedHelpQueriesHint:
          'Guardados quando enviou um pedido sem iniciar sessão. Abra um pedido para ver mensagens ou responder.',
        savedHelpQueriesLoading: 'A carregar…',
        webauthn: {
          notAllowed:
            'O início de sessão foi cancelado ou não foi permitido pelo seu dispositivo.',
          requestFailed: 'O pedido WebAuthn falhou.',
          genericError: 'Ocorreu um erro.',
        },
        authCard: {
          signInTitle: 'Iniciar sessão',
          createTitle: 'Criar conta',
          signInSubtitle: 'Aceda à sua conta INDMO',
          createSubtitle: 'Junte-se à comunidade INDMO',
          tabPassword: 'Email e palavra-passe',
          tabLink: 'Ligação por email',
          tabPasskey: 'Passkey',
          tabCreate: 'Criar conta',
          termsSignIn:
            'Ao iniciar sessão, aceita os termos de serviço e a política de privacidade do INDMO.',
          termsCreate:
            'Ao criar uma conta, aceita os termos de serviço e a política de privacidade do INDMO.',
          backToHome: '← Voltar ao início',
          rememberMe: 'Manter sessão',
          forgotPassword: 'Esqueceu-se da palavra-passe?',
          cancel: 'Cancelar',
          linkIntro:
            'Indique o seu email e enviaremos uma ligação de acesso segura.',
          passkeyIntro: 'Use uma passkey para um acesso rápido e seguro.',
          passkeyInfoTitle: 'Sobre as passkeys',
          passkeyInfoBody:
            'Use uma passkey guardada neste navegador ou dispositivo. Pode adicionar uma na página da conta depois de iniciar sessão de outra forma.',
          emailSentTitle: 'Email enviado',
          emailSentBody: 'Consulte a caixa de entrada. A ligação pode demorar um minuto.',
          signIn: 'Iniciar sessão',
          sendLoginLink: 'Enviar ligação de acesso',
          loginCodeLabel: 'Código',
          continuePasskey: 'Iniciar sessão com passkey',
          verifyAndSignIn: 'Verificar e iniciar sessão',
          createAccount: 'Criar conta',
          signingIn: 'A iniciar sessão…',
          sending: 'A enviar…',
          verifying: 'A verificar…',
          signingUp: 'A registar…',
          passkeyUnavailable: 'Não suportado neste navegador',
          differentSignInMethod: 'Utilizar outro método de início de sessão',
          mfaPasskeyRequiredTitle: 'Passkey necessária para concluir o acesso',
          mfaPasskeyRequiredBody:
            'A palavra-passe foi aceite. Utilize a sua passkey para completar o segundo passo.',
          mfaRequiredTitle: 'Verificação adicional necessária',
          mfaOtherFactorBody:
            'Conclua o início de sessão com a aplicação de autenticação ou um código de recuperação, conforme a sua conta.',
        },
        signInForm: {
          emailLabel: 'Email',
          emailPlaceholder: "o.seu.email{'@'}exemplo.com",
          emailInvalid: 'Introduza um endereço de email válido.',
          credentialsInvalid:
            'O endereço de email e/ou a palavra-passe que indicou não estão corretos.',
          codeInvalid: 'Código incorreto.',
          codeExpired:
            'O código é inválido ou expirou. Peça uma nova ligação de acesso.',
          passwordLabel: 'Palavra-passe',
          passwordPlaceholder: 'Introduza a sua palavra-passe',
        },
        signupForm: {
          title: 'Criar uma conta',
          emailLabel: 'Email',
          emailPlaceholder: "o.seu.email{'@'}exemplo.com",
          firstNameLabel: 'Nome',
          firstNamePlaceholder: 'Introduza o nome',
          lastNameLabel: 'Apelido',
          lastNamePlaceholder: 'Introduza o apelido',
          phoneLabel: 'Número de telefone',
          phonePlaceholder: '+670 XXXX XXXX',
          introductionLabel: 'Apresentação',
          introductionPlaceholder: 'Fale-nos um pouco sobre si...',
          optional: 'Opcional',
          submit: 'Registar-se',
          successMessage: 'Consulte o seu email para confirmar a sua conta.',
        },
        passwordReset: {
          requestTitle: 'Esqueci-me da palavra-passe',
          requestSubtitle:
            'Indique o seu email e enviaremos uma ligação para redefinir a palavra-passe.',
          emailLabel: 'Email',
          emailPlaceholder: "o.seu.email{'@'}exemplo.com",
          sendLink: 'Enviar ligação de redefinição',
          sending: 'A enviar…',
          backToSignIn: 'Voltar ao início de sessão',
          requestSuccessTitle: 'Consulte o seu email',
          requestSuccessBody:
            'Se existir uma conta com esse endereço, enviámos instruções para redefinir a palavra-passe.',
          confirmTitle: 'Definir nova palavra-passe',
          confirmSubtitle: 'Introduza uma nova palavra-passe para a sua conta.',
          confirmLoading: 'A verificar a ligação de redefinição…',
          newPasswordLabel: 'Nova palavra-passe',
          confirmPasswordLabel: 'Confirmar nova palavra-passe',
          setNewPassword: 'Redefinir palavra-passe',
          resetting: 'A guardar…',
          passwordsDoNotMatch: 'As palavras-passe não coincidem.',
          invalidOrExpiredLink: 'Esta ligação é inválida ou expirou.',
          showPassword: 'Mostrar palavra-passe',
          hidePassword: 'Ocultar palavra-passe',
        },
        verifyEmail: {
          title: 'Verificar o seu email',
          subtitle: 'Utilize a ligação que enviámos ou peça outro email de confirmação.',
          confirmPrompt: 'Clique no botão abaixo para confirmar o seu endereço de email.',
          confirmButton: 'Confirmar endereço de email',
          confirming: 'A confirmar o endereço de email…',
          invalidLink: 'Esta ligação de verificação é inválida.',
          invalidOrExpiredKey: 'Esta ligação de verificação é inválida ou expirou.',
          resendButton: 'Reenviar email de verificação',
          resending: 'A enviar…',
          resendSuccessTitle: 'Consulte o seu email',
          resendSuccessBody:
            'Se este navegador ainda tiver a sessão de registo, enviámos outra mensagem. Emails anteriores podem ainda ter uma ligação válida.',
          resendUnavailableTitle: 'Não foi possível reenviar a partir deste navegador',
          resendUnavailableBody:
            'Utilize o mesmo navegador onde se registou ou inicie sessão com o seu email para obter uma nova ligação.',
          continueHome: 'Continuar para o início',
          returnHome: 'Voltar ao início',
          backToSignup: 'Voltar ao registo',
          goToSignIn: 'Ir para início de sessão',
          alreadyVerified: 'O seu email já está verificado. Pode iniciar sessão na conta.',
          resendNetworkError: 'Não foi possível enviar agora. Tente novamente dentro de momentos.',
        },
        dashboard: {
          myAccount: 'A minha conta',
          subtitle: 'Gerir as definições e preferências da sua conta INDMO.',
          logout: 'Terminar sessão',
          accountDetails: 'Dados da conta',
          password: 'Palavra-passe',
          passkeys: 'Passkeys',
          backToHome: '← Voltar ao início',
          detailsTitle: 'Dados da conta',
          firstName: 'Nome próprio',
          lastName: 'Apelido',
          emailAddress: 'Endereço de email',
          phoneNumber: 'Número de telefone',
          associatedCentres: 'Centros de formação associados',
          passwordSecureHint:
            'A palavra-passe está definida e segura. Clique em "Alterar palavra-passe" para a atualizar.',
          passwordAddHint:
            'Ainda não tem palavra-passe definida. Clique em "Adicionar palavra-passe" para criar uma.',
          changePassword: 'Alterar palavra-passe',
          addPassword: 'Adicionar palavra-passe',
          currentPassword: 'Palavra-passe atual',
          passwordChangeCurrentRequired:
            'Introduza a sua palavra-passe atual.',
          confirmPassword: 'Confirmar palavra-passe',
          setPassword: 'Definir palavra-passe',
          passwordSubmitLoading: 'A atualizar…',
          passwordUpdatedSuccess: 'Palavra-passe atualizada com sucesso.',
          passwordTooShort:
            'Esta palavra-passe é demasiado curta. Tem de ter pelo menos {minLength} caracteres.',
          passkeysSubtitle: 'Autenticação segura sem palavra-passe',
          addPasskey: 'Adicionar passkey',
          passkeyNameLabel: 'Nome da passkey',
          loadingSession: 'A carregar a sessão…',
          passkeysEmpty:
            'Ainda não tem passkeys. Adicione uma para um acesso rápido e seguro.',
          passkeysManageHint:
            'Adicione uma passkey para iniciar sessão sem palavra-passe. Precisa de um email verificado na conta.',
          passkeyRemoveAriaLabel: 'Remover passkey',
          passkeyRenameAriaLabel: 'Alterar o nome da passkey {name}',
          passkeyRenameSaveAriaLabel: 'Guardar o novo nome da passkey {name}',
          passkeyRenameCancelAriaLabel: 'Cancelar a alteração do nome da passkey {name}',
          passkeyReauthSecurityMessage:
            'Por segurança, confirme com a palavra-passe da conta ou um código por email abaixo e continue a adicionar a passkey.',
          passkeyReauthSessionAgainMessage:
            'A sua sessão precisa de nova confirmação. Utilize a palavra-passe ou um código por email abaixo para continuar.',
          passkeyReauthTitle:
            'Confirme que é consigo para continuar a adicionar esta passkey.',
          passkeyReauthAccountPassword: 'Palavra-passe da conta',
          passkeyReauthOr: 'ou',
          passkeyReauthEmailHint:
            'Podemos enviar-lhe um código de utilização única para o endereço da sua conta.',
          passkeyReauthEmailCodeButton: 'Enviar código de confirmação por email',
          passkeyReauthEmailCodeButtonSending: 'A enviar…',
          passkeyReauthWorking: 'A processar…',
          passkeyReauthConfirmPasswordButton:
            'Confirmar palavra-passe e continuar',
          passkeyReauthVerifyCodeButton: 'Verificar código e continuar',
          passkeyReauthCodeLabel: 'Código',
          passkeyReauthEmailSentNoAddress: 'Consulte o seu email para obter um código.',
          passkeyReauthEmailSentWithAddress:
            'Consulte o seu email para obter um código enviado para {email}.',
          passkeyReauthIncorrectPassword: 'Palavra-passe incorreta.',
          passkeyReauthPasswordConfirmFailed:
            'Não foi possível confirmar a palavra-passe ({status}).',
          passkeyReauthSignInAgain:
            'Inicie sessão novamente e tente adicionar a passkey.',
          passkeyReauthSendCodeFailed:
            'Não foi possível enviar o código ({status}).',
          passkeyReauthVerifyFailed: 'A verificação falhou ({status}).',
          passkeyReauthGenericError: 'Ocorreu um erro.',
          emailVerifiedSuccess:
            'O seu email foi verificado. A sua conta será analisada pela equipa INDMO antes de ser aprovada.',
          dismissNotification: 'Fechar aviso',
          approvalPendingBanner:
            'A sua conta ainda não foi aprovada. Algumas áreas do site ficam limitadas até a equipa INDMO aprovar o seu registo.',
          cmsAccessDeniedBanner:
            'Esta conta não tem acesso ao CMS. Se precisar de acesso, contacte um administrador.',
          approvalStatusLabel: 'Aprovação da conta',
          approvalStatusApproved: 'Aprovada',
          approvalStatusPending: 'Pendente de aprovação',
          mySubmissions: 'Os meus pedidos',
          mySubmissionsTitle: 'Candidaturas e processos',
          mySubmissionsLoading: 'A carregar…',
          mySubmissionsError: 'Não foi possível carregar a lista de momento.',
          mySubmissionsEmpty: 'Ainda não tem candidaturas nem envios online.',
          mySubmissionsColType: 'Tipo',
          mySubmissionsColSummary: 'Resumo',
          mySubmissionsColStatus: 'Estado',
          mySubmissionsColSubmitted: 'Submetido',
          mySubmissionsColTask: 'Passo atual',
          mySubmissionsWorkflowInProgress: 'Em curso',
          mySubmissionsStaffComments: 'Mensagens da equipa',
          mySubmissionsStaffCommentsAria: 'Mensagens da equipa ({count})',
          mySubmissionsWorkflowMessages: 'Mensagens do processo',
          mySubmissionsMessageRoleStaff: 'Equipa',
          mySubmissionsMessageRoleYou: 'Consigo',
          mySubmissionsReplyLabel: 'A sua mensagem para o INDMO',
          mySubmissionsReplyPlaceholder: 'Escreva uma mensagem…',
          mySubmissionsReplySubmit: 'Enviar',
          mySubmissionsReplySending: 'A enviar…',
          mySubmissionsReplyError: 'Não foi possível enviar. Tente novamente.',
          submissionStatus: {
            submitted: 'Submetido',
            completed: 'Concluído',
            rejected: 'Rejeitado',
            pending_offline: 'Processo offline pendente',
          },
          workflowPushTitle: 'Notificações de fluxos (navegador)',
          workflowPushHint:
            'Receba alertas neste dispositivo quando houver tarefas de moderação ou atualizações de processo.',
          workflowPushEnable: 'Ativar notificações do navegador',
          workflowPushEnabled: 'Notificações do navegador ativas.',
          workflowPushUnavailable: 'Push não está configurado no servidor.',
          workflowPushError: 'Não foi possível ativar notificações. Tente novamente.',
          workflowPushNeedsSw:
            'As notificações do navegador precisam de uma build de produção com service worker (ex.: npm run build && vite preview) em HTTPS.',
          needHelpPushPrompt: 'Quer ser notificado quando respondermos?',
          needHelpPushButton: 'Ativar notificações',
          needHelpPushDone: 'Receberá alertas neste dispositivo quando houver resposta.',
          helpQueryPortalThankYouLink: 'Ver o seu pedido e as mensagens',
          helpQueryPortalThankYouHint:
            'Guardámos uma ligação segura neste navegador. Também pode marcar como favorito ou copiar o endereço da barra para usar mais tarde.',
          helpQueryStatusTitle: 'O seu pedido de ajuda',
          helpQueryStatusIntro:
            'Pode continuar a usar o INDMO. Se ativou notificações, avisaremos este dispositivo quando houver novidades.',
          helpQueryStatusInvalidLink:
            'Esta ligação é inválida ou expirou. Volte à página inicial ou envie um novo pedido de ajuda.',
          helpQueryStatusLoadError: 'Não foi possível carregar este pedido. Tente novamente.',
          helpQueryStatusBackHome: 'Voltar ao início',
          helpQueryStatusNoMessagesYet: 'Ainda não há mensagens neste separador.',
        },
      },
      siteUnderConstruction: {
        title: 'Site em construção',
        body:
          'O site público ainda não está disponível. O pessoal pode iniciar sessão abaixo ou abrir o admin do CMS.',
        accountLink: 'Entrar / A minha conta',
        adminLink: 'Admin CMS (Wagtail)',
      },
      sideNav: {
        howCanWeHelp: 'Como podemos ajudar?',
        followUs: 'Siga-nos',
        megaSectionIntro: 'Explorar esta secção',
      },
      partnersCarousel: {
        title: 'Os nossos parceiros',
        scrollLeft: 'Deslocar parceiros para a esquerda',
        scrollRight: 'Deslocar parceiros para a direita',
      },
      footer: {
        quickLinks: 'Ligações rápidas',
        resources: 'Recursos',
        contactUs: 'Contacte-nos',
        legalNav: 'Ligações legais',
        buildRelease: 'Release: {tag}',
        buildTime: 'Build: {time}',
        buildGit: 'Commit: {sha}',
        buildInfoUnavailable: 'Metadados de build indisponíveis (dev local ou imagem antiga).',
      },
      processFlow: {
        back: 'Voltar',
        next: 'Seguinte',
        stepOf: 'Passo {current} de {total}',
        submitApplication: 'Submeter candidatura',
        submitting: 'A submeter…',
        close: 'Fechar',
      },
      news: {
        featured: 'Em destaque',
        readingMinutes: '{n} min de leitura',
        readMore: 'Ler mais',
        showingRange: 'A mostrar {from}–{to} de {total} notícias',
        backToLatestNews: 'Voltar às últimas notícias',
        tagsHeading: 'Etiquetas:',
        shareHeading: 'Partilhe este artigo:',
        shareRegionAria: 'Partilhar este artigo',
        shareCopyLink: 'Copiar ligação',
        shareFacebook: 'Partilhar no Facebook',
        shareCopied: 'Ligação copiada para a área de transferência.',
        byline: 'Por {name}',
        previous: 'Anterior',
        next: 'Seguinte',
        pageOf: 'Página {current} de {total}',
        findNews: 'Encontrar notícias',
        searchLabel: 'Pesquisar',
        searchPlaceholder: 'Pesquisar notícias por título ou conteúdo…',
        category: 'Categoria',
        filterAll: 'Todos',
      },
      form: {
        filesLabel: 'Ficheiros',
        fileLabel: 'Ficheiro',
        fileRequired: 'Envie um ficheiro antes de submeter.',
        filesRequired: 'Envie pelo menos um ficheiro antes de submeter.',
        correctErrorsBelow: 'Corrija os erros abaixo.',
        uploadAriaLabel: 'Enviar ficheiros',
        uploadOneFileAriaLabel: 'Enviar ficheiro',
        clickToUpload: 'Clique para carregar',
        orDragAndDrop: ' ou arrastar e largar',
        uploadFileTypesHint: '{types} permitidos',
        maxHint: ' (máx. {max})',
        filesSelectedCount: '{count} ficheiro(s) selecionado(s):',
        removeFileAriaLabel: 'Remover ficheiro',
        thankYou: 'Obrigado pela sua submissão.',
        submit: 'Submeter',
        submitting: 'A submeter…',
      },
      trainingCentreList: {
        sectionAria: 'Diretório de organizações',
        filterHeading: 'Encontrar centros de formação',
        search: 'Pesquisar',
        searchPlaceholder: 'Pesquisar por nome…',
        district: 'Distrito',
        subdistrict: 'Subdistrito',
        allSubdistricts: 'Todos os subdistritos',
        level: 'Nível',
        all: 'Todos',
        courseCountOne: '1 curso',
        courseCountMany: '{count} cursos',
        showingCount: 'A mostrar {shown} de {total} centros',
        viewDetails: 'Ver detalhes',
        directoryMapTitle: 'Mapa de todos os centros',
        directoryMapSummaryAria: 'Mapa expansível com a localização de todos os centros de formação no diretório que tenham coordenadas',
        directoryMapCanvasAria: 'Mapa com as localizações dos centros de formação',
        noResultsTitle: 'Nenhum centro encontrado',
        noResultsHint: 'Tente ajustar a pesquisa ou os filtros',
        noTrainingCentres: 'Ainda não há organizações na lista.',
        loading: 'A carregar…',
        loadError: 'Não foi possível carregar a lista.',
        programsOfferedAria: 'Programas oferecidos',
      },
      programList: {
        sectionAria: 'Diretório de programas acreditados',
        filterHeading: 'Encontrar programas acreditados',
        search: 'Pesquisar',
        searchPlaceholder: 'Pesquisar por nome ou código…',
        offeredIn: 'Oferecido em',
        district: 'Distrito',
        subdistrict: 'Subdistrito',
        allSubdistricts: 'Todos os subdistritos',
        level: 'Nível do programa',
        all: 'Todos',
        showingCount: 'A mostrar {shown} de {total} programas',
        viewDetails: 'Ver detalhes',
        noResultsTitle: 'Nenhum programa encontrado',
        noResultsHint: 'Tente ajustar a pesquisa ou os filtros',
        noPrograms: 'Ainda não há programas na lista.',
        loading: 'A carregar…',
        loadError: 'Não foi possível carregar a lista.',
        centresOfferingAria: 'Centros de formação',
        trainingCentresCountOne: '1 centro de formação',
        trainingCentresCountMany: '{count} centros de formação',
        directoryMapTitle: 'Mapa dos centros de formação',
        directoryMapSummaryAria:
          'Mapa expansível com a localização dos centros de formação que oferecem os programas atualmente listados',
        directoryMapCanvasAria: 'Mapa com as localizações dos centros de formação dos programas filtrados',
      },
      programCertificateTier: {
        I: 'Certificado I',
        II: 'Certificado II',
        III: 'Certificado III',
        IV: 'Certificado IV',
      },
      programAccreditationClassification: 'Classificação {letter}',
      trainingCentreDetail: {
        summaryAria: 'Resumo da organização',
        code: 'Código',
        accreditation: 'Acreditação',
        locations: 'Localizações',
        programsOffered: 'Programas oferecidos',
        programCode: 'Código do programa',
        accreditationNoProgram: 'Número de acreditação do programa',
        accreditationNo: 'Número de acreditação',
        validFrom: 'Início',
        validTo: 'Fim',
        comment: 'Comentário',
        notListed: 'Esta organização não consta no diretório público.',
        loadError: 'Não foi possível carregar os detalhes da organização.',
        unavailableOffline: 'Ligue-se à internet para carregar os detalhes completos da organização.',
        loading: 'A carregar…',
        backToDirectory: 'Voltar aos centros de formação',
        aboutThisCentre: 'Sobre este centro',
        contactInformation: 'Informações de contacto',
        locationMapTitle: 'Mapa da localização',
        mapPinFallback: 'Local',
        addressLabel: 'Morada',
        centreStatistics: 'Estatísticas do centro',
        programsStatLabel: 'Programas',
        certificateLevelsStatLabel: 'Níveis de certificado',
        searchProgramsPlaceholder: 'Pesquisar programas…',
        allStatuses: 'Todos os estados',
        filterStatusAccredited: 'Acreditado',
        filterStatusNonAccredited: 'Não acreditado',
        allLevels: 'Todos os níveis',
        allDates: 'Todas as datas',
        filterDatesActive: 'Ativos neste momento',
        filterDatesExpiring: 'A expirar em breve',
        loadMorePrograms: 'Carregar mais programas',
        programsCountOne: '1 programa',
        programsCountMany: '{count} programas',
        noProgramsFilteredTitle: 'Nenhum programa encontrado',
        noProgramsFilteredHint: 'Tente ajustar os filtros',
        accreditationFieldLabel: 'Acreditação',
        datesFieldLabel: 'Datas',
        centreNotFoundTitle: 'Centro não encontrado',
        centreNotFoundBody:
          'O centro de formação que procura não existe ou foi removido.',
        programChipAccredited: 'Acreditado',
        programChipLicensed: 'Licenciado',
        programChipNonAccredited: 'Não acreditado',
        certificateLevelRangeNa: 'N/D',
      },
      programDetail: {
        summaryAria: 'Resumo do programa',
        code: 'Código',
        certificateLevel: 'Nível do certificado',
        trainingCentres: 'Centros de formação',
        noCentres: 'Não há centros de formação listados para este programa no diretório.',
        notFound: 'Programa não encontrado.',
        notFoundBody: 'O programa que procura não existe ou foi removido.',
        loadError: 'Não foi possível carregar os detalhes do programa.',
        unavailableOffline: 'Ligue-se à internet para carregar os detalhes completos do programa.',
        loading: 'A carregar…',
        backToPrograms: 'Voltar aos programas',
        aboutThisProgram: 'Sobre este programa',
        centresOfferingHeading: 'Centros de formação que oferecem este programa',
        searchCentresPlaceholder: 'Pesquisar centros…',
        allMunicipalities: 'Todos os municípios',
        centresListedCountOne: '1 centro',
        centresListedCountMany: '{count} centros',
        loadMoreCentres: 'Carregar mais centros',
        noCentresFilteredTitle: 'Nenhum centro encontrado',
        noCentresFilteredHint: 'Tente ajustar os filtros',
        directoryMapTitle: 'Mapa dos centros de formação',
        directoryMapSummaryAria:
          'Mapa expansível com a localização dos centros de formação que oferecem este programa',
        directoryMapCanvasAria: 'Mapa com as localizações dos centros de formação deste programa',
        competenciesHeading: 'Competências',
        noCompetencies: 'Ainda não há competências associadas a este programa.',
        competencyHours: '{hours} h',
      },
      certificateVerify: {
        title: 'Verificar certificado',
        subtitle: 'Confirme se este certificado está registado no INDMO.',
        validating: 'A validar…',
        successTitle: 'Certificado verificado com sucesso',
        successSubtitle: 'Este certificado é autêntico e foi emitido pelo INDMO.',
        detailsHeading: 'Detalhes do certificado',
        nameLabel: 'Nome',
        dateIssued: 'Data de emissão',
        program: 'Programa',
        trainingCentre: 'Centro de formação',
        invalidMessage: 'Esta ligação é inválida ou o certificado não foi encontrado.',
        networkError: 'Não foi possível ligar. Tente novamente.',
        returnHome: 'Voltar à página inicial',
      },
      blocks: {
        authorisationRequired: {
          createAccount: 'Criar uma conta',
          goToSubmissions: 'Ir para as minhas candidaturas e submissões',
          goToAccount: 'Ir para a conta',
        },
      },
    },
  },
});

export default i18n;
